/**
 * RepConnect - Supabase Data Services
 * 
 * Replaces all localStorage-based data functions with Supabase calls
 * Maintains the same API for easy migration
 */

import { supabase } from './supabase'
import { logger } from './logger';

/**
 * User Service - replaces UserStorage functions
 */
export const userService = {
  getAllUsers: async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error getting all users', { error: error.message })
      return []
    }
  },

  getCustomersOnly: async () => {
    try {

      // Check current user auth status

      // Query user_profiles with location fields directly
      let { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_type', 'customer')
        .order('created_at', { ascending: false })

      logger.debug('Customer query result', {
        dataCount: data?.length,
        errorCode: error?.code,
        errorMessage: error?.message
      });

      // If RLS is blocking and we have no results, try a test query
      if (!data || data.length === 0) {

        // Test if we can access any user_profiles at all
        const { data: testData, error: testError } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, user_type')
          .limit(5);

        logger.debug('Test query result', {
          testDataCount: testData?.length,
          hasError: !!testError,
          testData: testData
        });

        if (testData && testData.length > 0) {

          // Filter customers client-side as fallback
          const customers = testData.filter(profile => profile.user_type === 'customer');

          if (customers.length > 0) {
            // Fetch full customer data
            const customerIds = customers.map(c => c.id);
            const { data: fullCustomerData } = await supabase
              .from('user_profiles')
              .select('*')
              .in('id', customerIds);

            return fullCustomerData || [];
          }
        }
      }

      if (error) {
        logger.error('Database error details', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });

        // Don't throw error, return empty array to allow fallback

        return [];
      }

      return data || []
    } catch (error) {
      logger.error('Error getting customer users', { error: error.message })
      return []
    }
  },

  getUserById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error) {
        if (error.code === 'PGRST116') return null // No rows found
        throw error
      }
      
      return data
    } catch (error) {
      logger.error('Error getting user by ID', { error: error.message, id })
      return null
    }
  },

  getUserByEmail: async (email) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', email)
        .single()

      if (error) {
        if (error.code === 'PGRST116') return null // No rows found
        throw error
      }

      return data
    } catch (error) {
      logger.error('Error getting user by email', { error: error.message, email })
      return null
    }
  },

  createUser: async (userData) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          ...userData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error creating user', { error: error.message })
      throw error
    }
  },

  updateUser: async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error updating user', { error: error.message, id })
      throw error
    }
  }
}

/**
 * Meeting Service - replaces MeetingStorage functions
 */
export const meetingService = {
  getAllMeetings: async (userId = null, userType = null) => {
    try {
      logger.debug('getAllMeetings called', { userId, userType });

      // Simplified query without joins to avoid schema cache issues
      let query = supabase
        .from('meetings')
        .select('*')
        .order('meeting_date', { ascending: true })

      // Filter by user involvement if specified
      if (userId && userType) {
        if (userType === 'sales') {
          logger.debug('Filtering meetings for sales rep', { userId });
          query = query.eq('sales_rep_id', userId)
        } else if (userType === 'customer' || userType === 'office_staff') {
          console.log('🔍 getAllMeetings: Querying attendees for userId:', userId, 'userType:', userType);
          logger.debug('Filtering meetings for customer via RPC function', { userId });

          // For customers and office staff, use SECURITY DEFINER function to bypass RLS
          // Regular JOINs are blocked by meetings table RLS even when querying from meeting_attendees
          const { data: meetingsData, error: rpcError } = await supabase
            .rpc('get_meetings_for_attendee', { user_id_param: userId });

          console.log('🔍 getAllMeetings: Meetings found via RPC:', meetingsData?.length || 0);

          if (rpcError) {
            console.error('❌ Error calling get_meetings_for_attendee:', rpcError);
            logger.error('Error calling get_meetings_for_attendee RPC', { error: rpcError.message, userId });
            return [];
          }

          if (!meetingsData || meetingsData.length === 0) {
            console.log('⚠️ No meetings found for user');
            logger.debug('No meetings found for user via RPC', { userId });
            return [];
          }

          // Transform RPC result to match expected meeting format
          const meetings = meetingsData.map(record => ({
            id: record.meeting_id,
            sales_rep_id: record.sales_rep_id,
            date: record.meeting_date,  // Dashboard expects 'date'
            meeting_date: record.meeting_date,
            time: record.start_time,    // Dashboard expects 'time'
            start_time: record.start_time,
            end_time: record.end_time,
            status: record.status,
            restaurant_name: record.restaurant_name,
            restaurant_address: record.restaurant_address,
            restaurant_phone: record.restaurant_phone,
            restaurant_cuisine_type: record.restaurant_cuisine_type,
            restaurant_price_range: record.restaurant_price_range,
            restaurant_rating: record.restaurant_rating,
            restaurant_url: record.restaurant_url,
            restaurant_yelp_id: record.restaurant_yelp_id,
            estimated_cost: record.estimated_cost,
            actual_cost: record.actual_cost,
            attendee_count: record.attendee_count,
            meeting_notes: record.meeting_notes,
            meeting_type: record.meeting_type,
            created_at: record.created_at,
            updated_at: record.updated_at,
            customer_id: record.customer_id,
            location_id: record.location_id,
            location_name: record.location_name,
            location_address: record.location_address,
            lunch_ordered: record.lunch_ordered,
            selected_staff_ids: record.selected_staff_ids,
            user_rsvp_status: record.rsvp_status,
            user_is_organizer: record.is_organizer,
            // External sales rep fields (for manually added meetings)
            external_sales_rep_name: record.external_sales_rep_name,
            external_sales_rep_company: record.external_sales_rep_company,
            external_sales_rep_email: record.external_sales_rep_email,
            external_sales_rep_phone: record.external_sales_rep_phone,
            is_manually_added: record.is_manually_added
          }));

          console.log('🔍 getAllMeetings: Transformed meetings:', meetings.length);
          logger.debug('Retrieved meetings via RPC function', { userId, meetingsCount: meetings.length });

          // Already sorted by meeting_date in the function
          return meetings;
        } else if (userType === 'admin') {
          logger.debug('Filtering meetings for admin user', { userId });

          // Check auth status before querying admin_users
          const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
          logger.debug('Auth status for admin query', {
            authUserId: authUser?.id,
            authError: authError?.message,
            requestedUserId: userId,
            idsMatch: authUser?.id === userId
          });

          // For admin users, first look up their customer_id from admin_users table
          const { data: adminRelationship, error: adminError } = await supabase
            .from('admin_users')
            .select('customer_id')
            .eq('staff_id', userId)
            .single();

          if (adminError) {
            logger.error('Error looking up admin relationship', { error: adminError.message, userId });
            return [];
          }

          if (!adminRelationship || !adminRelationship.customer_id) {
            logger.warn('No customer relationship found for admin user', { userId, adminRelationship });
            return [];
          }

          logger.debug('Admin user manages customer', { adminUserId: userId, customerId: adminRelationship.customer_id });

          // Query meetings for the customer this admin manages
          query = query.eq('customer_id', adminRelationship.customer_id);
        }
      } else {
        logger.debug('Getting all meetings (no filter)');
      }

      const { data, error } = await query

      if (error) {
        logger.error('Supabase query error', { error: error.message });
        throw error;
      }

      logger.debug('getAllMeetings returned', { meetingsCount: data?.length || 0 });
      return data || []
    } catch (error) {
      logger.error('Error getting meetings', { error: error.message })
      return []
    }
  },

  getMeetingById: async (id, userId = null) => {
    try {
      logger.debug('getMeetingById called', { id, userId });

      // If userId provided, use RPC function to bypass RLS
      if (userId) {
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_meeting_by_id_for_user', {
            meeting_id_param: id,
            user_id_param: userId
          });

        if (rpcError) {
          logger.error('getMeetingById RPC error', { error: rpcError.message, id, userId });
          if (rpcError.code === 'PGRST116') return null;
          throw rpcError;
        }

        if (!rpcData || rpcData.length === 0) {
          logger.debug('getMeetingById: Meeting not found or user not authorized', { id, userId });
          return null;
        }

        // RPC returns array, get first result
        const meeting = rpcData[0];
        logger.debug('getMeetingById found meeting via RPC', { meetingId: meeting.id });

        // RPC doesn't include joined data, so fetch sales_rep and customer separately
        if (meeting.sales_rep_id) {
          const { data: salesRepData } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', meeting.sales_rep_id)
            .single();

          if (salesRepData) {
            meeting.sales_rep = salesRepData;
          }
        }

        if (meeting.customer_id) {
          const { data: customerData } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', meeting.customer_id)
            .single();

          if (customerData) {
            meeting.customer = customerData;
          }
        }

        return meeting;
      }

      // Original query for sales reps or when userId not provided
      const { data, error } = await supabase
        .from('meetings')
        .select(`
          *,
          customer:user_profiles!customer_id(*),
          sales_rep:user_profiles!sales_rep_id(*)
        `)
        .eq('id', id)
        .single()

      if (error) {
        logger.error('getMeetingById error', { error: error.message, id });
        if (error.code === 'PGRST116') return null
        throw error
      }

      logger.debug('getMeetingById found meeting', { meetingId: data?.id, hasSalesRep: !!data?.sales_rep });
      return data
    } catch (error) {
      logger.error('Error getting meeting by ID', { error: error.message, id })
      return null
    }
  },

  createMeeting: async (meetingData) => {
    try {
      const { data, error } = await supabase
        .from('meetings')
        .insert([{
          ...meetingData,
          created_at: new Date().toISOString()
        }])
        .select(`
          *,
          customer:user_profiles!customer_id(*),
          sales_rep:user_profiles!sales_rep_id(*)
        `)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error creating meeting', { error: error.message })
      throw error
    }
  },

  updateMeeting: async (id, updates) => {
    try {
      logger.debug('updateMeeting called', { id, hasUpdates: !!updates });

      // Check if user is authenticated for RLS
      const { data: { user } } = await supabase.auth.getUser();
      logger.debug('Current authenticated user for RLS', { userId: user?.id || 'NOT AUTHENTICATED' });

      if (!user) {
        logger.warn('No authenticated user - RLS policies will fail');
      }

      // First, do a simple update without the complex select
      const { data: updateData, error: updateError } = await supabase
        .from('meetings')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()

      if (updateError) {
        logger.error('Supabase updateMeeting error', {
          code: updateError.code,
          message: updateError.message,
          id
        });
        throw updateError;
      }

      logger.debug('Basic update succeeded', { meetingId: id });

      // If basic update succeeded, fetch the full meeting with joins
      if (updateData && updateData.length > 0) {
        try {
          const { data: fullMeeting, error: fetchError } = await supabase
            .from('meetings')
            .select(`
              *,
              customer:user_profiles!customer_id(
                first_name,
                last_name,
                email,
                phone,
                company,
                profile_picture
              ),
              sales_rep:user_profiles!sales_rep_id(
                first_name,
                last_name,
                email,
                phone,
                company,
                territory,
                profile_picture
              )
            `)
            .eq('id', id)
            .single()

          if (!fetchError && fullMeeting) {
            logger.debug('updateMeeting with full data succeeded', { meetingId: id });
            return fullMeeting;
          }
        } catch (fetchError) {
          logger.warn('Could not fetch full meeting details, returning basic update', { error: fetchError.message });
        }

        // Return the basic update if the full fetch fails
        return updateData[0];
      }

      throw new Error('Update returned no data');
    } catch (error) {
      logger.error('Error updating meeting', { error: error.message, id })
      throw error
    }
  },

  deleteMeeting: async (id) => {
    try {
      const { error } = await supabase
        .from('meetings')
        .delete()
        .eq('id', id)

      if (error) throw error
      return true
    } catch (error) {
      logger.error('Error deleting meeting', { error: error.message, id })
      throw error
    }
  },

  /**
   * Phase 2: Multi-Attendee Meeting Support
   * These functions leverage the meeting_attendees table for multi-physician meetings
   */

  /**
   * Get meeting with all attendees
   * @param {string} meetingId - UUID of the meeting
   * @returns {Promise<Object|null>} Meeting with attendees array
   */
  getMeetingWithAttendees: async (meetingId) => {
    try {
      // Get meeting details
      const { data: meeting, error: meetingError } = await supabase
        .from('meetings')
        .select(`
          *,
          customer:user_profiles!customer_id(*),
          sales_rep:user_profiles!sales_rep_id(*)
        `)
        .eq('id', meetingId)
        .single();

      if (meetingError) {
        if (meetingError.code === 'PGRST116') return null;
        throw meetingError;
      }

      // Get attendees
      const { data: attendees, error: attendeesError } = await supabase
        .from('meeting_attendees')
        .select(`
          *,
          user_profiles:user_id (
            id,
            first_name,
            last_name,
            email,
            specialty,
            user_type
          )
        `)
        .eq('meeting_id', meetingId)
        .order('is_organizer', { ascending: false })
        .order('attendee_type', { ascending: true });

      if (attendeesError) throw attendeesError;

      // Combine meeting with attendees
      return {
        ...meeting,
        attendees: attendees || []
      };
    } catch (error) {
      logger.error('Error getting meeting with attendees:', error);
      throw error;
    }
  },

  /**
   * Get all meetings for a user (via meeting_attendees table)
   * This supports multi-physician meetings where user might not be customer_id
   * @param {string} userId - UUID of the user
   * @param {string} userType - Type of user ('sales', 'customer', etc.)
   * @returns {Promise<Array>} Array of meetings
   */
  getAllMeetingsForUser: async (userId, userType) => {
    try {
      let meetings = [];

      if (userType === 'sales') {
        // Sales reps: meetings where they are sales_rep_id
        const { data, error } = await supabase
          .from('meetings')
          .select('*')
          .eq('sales_rep_id', userId)
          .order('meeting_date', { ascending: true });

        if (error) throw error;
        meetings = data || [];
      } else if (userType === 'customer' || userType === 'office_staff') {
        // Physicians/staff: meetings via meeting_attendees table
        const { data, error } = await supabase
          .from('meeting_attendees')
          .select(`
            *,
            meetings!inner (
              id,
              meeting_date,
              start_time,
              end_time,
              status,
              location_id,
              location_name,
              location_address,
              restaurant_name,
              restaurant_address,
              total_cost,
              sales_rep_id,
              customer_id,
              created_at,
              updated_at
            )
          `)
          .eq('user_id', userId)
          .order('meetings(meeting_date)', { ascending: true });

        if (error) throw error;

        // Extract meetings from attendees
        meetings = (data || []).map(attendee => ({
          ...attendee.meetings,
          user_rsvp_status: attendee.rsvp_status,
          user_is_organizer: attendee.is_organizer
        }));
      }

      return meetings;
    } catch (error) {
      logger.error('Error getting all meetings for user:', error);
      return [];
    }
  },

  /**
   * Create multi-physician meeting with attendees
   * @param {Object} meetingData - Meeting details
   * @param {Array} attendees - Array of attendee objects with user_id, attendee_type, etc.
   * @returns {Promise<Object>} Created meeting with attendees
   */
  createMeetingWithAttendees: async (meetingData, attendees = []) => {
    try {
      // Create the meeting
      const { data: meeting, error: meetingError } = await supabase
        .from('meetings')
        .insert([{
          ...meetingData,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (meetingError) throw meetingError;

      // If no attendees provided, create default attendees (physician + sales rep)
      let attendeeRecords = attendees;
      if (attendees.length === 0 && meetingData.customer_id && meetingData.sales_rep_id) {
        attendeeRecords = [
          {
            user_id: meetingData.customer_id,
            attendee_type: 'physician',
            is_organizer: true,
            rsvp_status: 'pending'
          },
          {
            user_id: meetingData.sales_rep_id,
            attendee_type: 'sales_rep',
            is_organizer: false,
            rsvp_status: 'accepted'
          }
        ];
      }

      // Create attendee records
      if (attendeeRecords.length > 0) {
        const attendeeInserts = attendeeRecords.map(attendee => ({
          meeting_id: meeting.id,
          ...attendee
        }));

        const { error: attendeesError } = await supabase
          .from('meeting_attendees')
          .insert(attendeeInserts);

        if (attendeesError) {
          logger.error('Error creating attendees, rolling back meeting:', attendeesError);
          // Rollback: delete the meeting
          await supabase.from('meetings').delete().eq('id', meeting.id);
          throw attendeesError;
        }
      }

      logger.info('Meeting created with attendees:', {
        meetingId: meeting.id,
        attendeeCount: attendeeRecords.length
      });

      return meeting;
    } catch (error) {
      logger.error('Error creating meeting with attendees:', error);
      throw error;
    }
  }
}

/**
 * Provider Service - healthcare provider management
 */
export const providerService = {
  getAllProviders: async () => {
    try {
      const { data, error } = await supabase
        .from('healthcare_providers')
        .select(`
          *,
          user_profiles(*)
        `)
        .order('practice_name', { ascending: true })
      
      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error getting providers', { error: error.message })
      return []
    }
  },

  getProviderById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('healthcare_providers')
        .select(`
          *,
          user_profiles(*)
        `)
        .eq('id', id)
        .single()

      if (error) {
        if (error.code === 'PGRST116') return null
        throw error
      }

      return data
    } catch (error) {
      logger.error('Error getting provider by ID', { error: error.message, id })
      return null
    }
  },

  getProviderByUserId: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('healthcare_providers')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') return null
        throw error
      }

      return data
    } catch (error) {
      logger.error('Error getting provider by user ID', { error: error.message, userId })
      return null
    }
  },

  createProvider: async (providerData) => {
    try {
      const { data, error } = await supabase
        .from('healthcare_providers')
        .insert([providerData])
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error creating provider', { error: error.message })
      throw error
    }
  },

  updateProvider: async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('healthcare_providers')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error updating provider', { error: error.message, id })
      throw error
    }
  },

  searchProviders: async (searchTerm) => {
    try {
      const { data, error } = await supabase
        .from('healthcare_providers')
        .select(`
          *,
          user_profiles(*)
        `)
        .or(`practice_name.ilike.%${searchTerm}%,specialty.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`)
        .order('practice_name', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error searching providers', { error: error.message, searchTerm })
      return []
    }
  }
}

/**
 * Restaurant Service
 */
export const restaurantService = {
  getAllRestaurants: async () => {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .order('name', { ascending: true })
      
      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error getting restaurants', { error: error.message })
      return []
    }
  },

  getRestaurantById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error) {
        if (error.code === 'PGRST116') return null
        throw error
      }
      
      return data
    } catch (error) {
      logger.error('Error getting restaurant by ID', { error: error.message, id })
      return null
    }
  },

  searchRestaurants: async (searchTerm) => {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .or(`name.ilike.%${searchTerm}%,cuisine_type.ilike.%${searchTerm}%`)
        .order('name', { ascending: true })
      
      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error searching restaurants', { error: error.message, searchTerm })
      return []
    }
  }
}

/**
 * Real-time subscriptions
 */
export const realtimeService = {
  subscribeToMeetings: (userId, userType, callback) => {
    const channel = supabase
      .channel('meeting-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'meetings'
        }, 
        (payload) => {
          callback(payload)
        }
      )
      .subscribe()

    return channel
  },

  subscribeToMeetingRequests: (userId, callback) => {
    const channel = supabase
      .channel('meeting-request-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'meeting_requests'
        }, 
        (payload) => {
          callback(payload)
        }
      )
      .subscribe()

    return channel
  },

  unsubscribe: (channel) => {
    return supabase.removeChannel(channel)
  }
}

/**
 * Customer Data Services - comprehensive customer information management
 */
export const customerDataService = {
  // Dining Preferences
  getDiningPreferences: async (providerId) => {
    try {
      const { data, error } = await supabase
        .from('provider_dining_preferences')
        .select('*')
        .eq('provider_id', providerId)
        .single()
      
      if (error && error.code !== 'PGRST116') throw error
      return data
    } catch (error) {
      logger.error('Error getting dining preferences', { error: error.message, providerId })
      return null
    }
  },

  saveDiningPreferences: async (providerId, preferences) => {
    try {
      const { data, error } = await supabase
        .from('provider_dining_preferences')
        .upsert({
          provider_id: providerId,
          ...preferences,
          updated_at: new Date().toISOString()
        })
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error saving dining preferences', { error: error.message, providerId })
      throw error
    }
  },

  // Availability Settings
  getAvailability: async (providerId) => {
    try {
      // Query for general availability (location_id IS NULL)
      // This maintains backward compatibility
      const { data, error } = await supabase
        .from('provider_availability')
        .select('*')
        .eq('provider_id', providerId)
        .is('location_id', null)
        .maybeSingle() // Use maybeSingle instead of single - returns null if no match

      if (error) throw error

      // Log the raw data to debug
      logger.debug('Raw availability data from Supabase', { hasData: !!data, providerId });

      return data
    } catch (error) {
      logger.error('Error getting availability', { error: error.message, providerId })
      return null
    }
  },

  saveAvailability: async (providerId, availability) => {
    try {
      const { data, error } = await supabase
        .from('provider_availability')
        .upsert({
          provider_id: providerId,
          ...availability,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'provider_id'  // Update existing row if provider_id matches
        })
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error saving availability', { error: error.message, providerId })
      throw error
    }
  },

  // Location-based availability methods
  getAvailabilityByLocation: async (providerId, locationId) => {
    try {
      // First try to get availability directly for this provider
      let { data, error } = await supabase
        .from('provider_availability')
        .select('*')
        .eq('provider_id', providerId)
        .eq('location_id', locationId)
        .maybeSingle() // Use maybeSingle - returns null if no match

      if (error) throw error

      // If found, return it
      if (data) {
        logger.debug('Loaded location-specific availability', { hasData: true, providerId, locationId });
        return data;
      }

      // No availability found - check if this user is office_staff delegating for a physician
      // If so, use the physician's availability
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_type, delegating_for')
        .eq('id', providerId)
        .maybeSingle();

      // Handle case where profile query fails (not found is ok, other errors should be logged)
      if (profileError && profileError.code !== 'PGRST116') {
        logger.warn('Error fetching user profile for availability check', { error: profileError.message, providerId });
      }

      if (userProfile?.user_type === 'office_staff' && userProfile?.delegating_for) {
        logger.debug('User is office_staff delegating for physician, checking physician availability', {
          staffId: providerId,
          physicianId: userProfile.delegating_for
        });

        const { data: physicianAvailability, error: physicianError } = await supabase
          .from('provider_availability')
          .select('*')
          .eq('provider_id', userProfile.delegating_for)
          .eq('location_id', locationId)
          .maybeSingle();

        if (!physicianError && physicianAvailability) {
          logger.info('Using availability from delegated physician', {
            staffId: providerId,
            physicianId: userProfile.delegating_for,
            locationId
          });
          return physicianAvailability;
        }
      }

      logger.debug('No availability found for provider or delegated physician', { providerId, locationId });
      return null;
    } catch (error) {
      logger.error('Error getting location availability', { error: error.message, providerId, locationId })
      return null
    }
  },

  getAllLocationAvailabilities: async (providerId) => {
    try {
      const { data, error } = await supabase
        .from('provider_availability')
        .select('*')
        .eq('provider_id', providerId)
        .order('location_id', { ascending: true, nullsFirst: true })

      if (error) throw error

      logger.debug('Loaded all location availabilities', { count: data?.length || 0, providerId });
      return data || []
    } catch (error) {
      logger.error('Error getting all location availabilities', { error: error.message, providerId })
      return []
    }
  },

  saveLocationAvailability: async (providerId, locationId, availability) => {
    try {
      // Check if this user is office_staff delegating for a physician
      // If so, save under the physician's ID, not the office staff's ID
      let targetProviderId = providerId;

      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_type, delegating_for')
        .eq('id', providerId)
        .maybeSingle();

      if (!profileError && userProfile?.user_type === 'office_staff' && userProfile?.delegating_for) {
        logger.debug('User is office_staff delegating for physician, saving under physician ID', {
          staffId: providerId,
          physicianId: userProfile.delegating_for
        });
        targetProviderId = userProfile.delegating_for;
      }

      // Try RPC function first to bypass RLS
      const { data: rpcData, error: rpcError } = await supabase.rpc('upsert_provider_availability', {
        provider_id_param: targetProviderId,
        location_id_param: locationId,
        weekly_availability_param: availability.weekly_availability || null,
        meeting_preferences_param: availability.meeting_preferences || null,
        blocked_periods_param: availability.blocked_periods || null
      });

      if (!rpcError) {
        logger.debug('Saved location availability via RPC', { targetProviderId, locationId });
        return rpcData;
      }

      // Fallback to direct upsert if RPC doesn't exist
      logger.warn('RPC upsert_provider_availability failed, trying direct upsert:', rpcError);

      const { data, error } = await supabase
        .from('provider_availability')
        .upsert({
          provider_id: targetProviderId,
          location_id: locationId,
          ...availability,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'provider_id,location_id'
        })
        .select()
        .single()

      if (error) throw error
      logger.debug('Saved location availability', { targetProviderId, locationId });
      return data
    } catch (error) {
      logger.error('Error saving location availability', { error: error.message, providerId, locationId })
      throw error
    }
  },

  deleteLocationAvailability: async (providerId, locationId) => {
    try {
      const { error } = await supabase
        .from('provider_availability')
        .delete()
        .eq('provider_id', providerId)
        .eq('location_id', locationId)

      if (error) throw error
      logger.debug('Deleted location availability', { providerId, locationId });
    } catch (error) {
      logger.error('Error deleting location availability', { error: error.message, providerId, locationId })
      throw error
    }
  },

  // Profile Enhancements (Sales Rep Notes)
  getProfileEnhancements: async (providerId, salesRepId) => {
    try {
      const { data, error } = await supabase
        .from('provider_profile_enhancements')
        .select('*')
        .eq('provider_id', providerId)
        .eq('sales_rep_id', salesRepId)
        .order('updated_at', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error getting profile enhancements', { error: error.message, providerId, salesRepId })
      return []
    }
  },

  saveProfileEnhancement: async (providerId, salesRepId, enhancement) => {
    try {
      const { data, error } = await supabase
        .from('provider_profile_enhancements')
        .upsert({
          provider_id: providerId,
          sales_rep_id: salesRepId,
          ...enhancement,
          updated_at: new Date().toISOString()
        })
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error saving profile enhancement', { error: error.message, providerId, salesRepId })
      throw error
    }
  },

  // Approved Sales Reps
  getApprovedReps: async (providerId) => {
    try {
      const { data, error } = await supabase
        .from('provider_approved_reps')
        .select(`
          *,
          user_profiles(*)
        `)
        .eq('provider_id', providerId)
        .eq('approval_status', 'approved')
      
      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error getting approved reps', { error: error.message, providerId })
      return []
    }
  },

  saveApprovedReps: async (providerId, repIds) => {
    try {
      // First, mark all existing as not approved
      await supabase
        .from('provider_approved_reps')
        .update({ approval_status: 'blocked' })
        .eq('provider_id', providerId)
      
      // Then upsert the approved ones
      const upsertData = repIds.map(repId => ({
        provider_id: providerId,
        sales_rep_id: repId,
        approval_status: 'approved',
        approval_date: new Date().toISOString()
      }))
      
      const { data, error } = await supabase
        .from('provider_approved_reps')
        .upsert(upsertData)
        .select()
      
      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error saving approved reps', { error: error.message, providerId })
      throw error
    }
  },

  // Office Staff Management
  getOfficeStaff: async (providerId) => {
    try {
      const { data, error } = await supabase
        .from('provider_office_staff')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: true })
      
      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error getting office staff', { error: error.message, providerId })
      return []
    }
  },

  saveOfficeStaff: async (providerId, staffData) => {
    try {
      const { data, error } = await supabase
        .from('provider_office_staff')
        .insert({
          provider_id: providerId,
          ...staffData
        })
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error saving office staff', { error: error.message, providerId })
      throw error
    }
  },

  updateOfficeStaff: async (staffId, updates) => {
    try {
      const { data, error } = await supabase
        .from('provider_office_staff')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', staffId)
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error updating office staff', { error: error.message, staffId })
      throw error
    }
  },

  deleteOfficeStaff: async (staffId) => {
    try {
      const { error } = await supabase
        .from('provider_office_staff')
        .delete()
        .eq('id', staffId)
      
      if (error) throw error
      return true
    } catch (error) {
      logger.error('Error deleting office staff', { error: error.message, staffId })
      throw error
    }
  },

  // Meeting Interactions and Notes
  getMeetingInteractions: async (meetingId) => {
    try {
      const { data, error } = await supabase
        .from('meeting_interactions')
        .select(`
          *,
          user_profiles(first_name, last_name)
        `)
        .eq('meeting_id', meetingId)
        .order('created_at', { ascending: true })
      
      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error getting meeting interactions', { error: error.message, meetingId })
      return []
    }
  },

  saveMeetingInteraction: async (interactionData) => {
    try {
      const { data, error } = await supabase
        .from('meeting_interactions')
        .insert(interactionData)
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error saving meeting interaction', { error: error.message })
      throw error
    }
  },

  // Contact History
  getContactHistory: async (providerId, salesRepId) => {
    try {
      const { data, error } = await supabase
        .from('provider_contact_history')
        .select('*')
        .eq('provider_id', providerId)
        .eq('sales_rep_id', salesRepId)
        .order('contact_date', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      logger.error('Error getting contact history', { error: error.message, providerId, salesRepId })
      return []
    }
  },

  saveContactHistory: async (contactData) => {
    try {
      const { data, error } = await supabase
        .from('provider_contact_history')
        .insert(contactData)
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      logger.error('Error saving contact history', { error: error.message })
      throw error
    }
  },

  // Comprehensive provider data sync
  syncAllProviderData: async (providerId, allData) => {
    try {
      const results = {}
      
      // Sync dining preferences
      if (allData.diningPreferences) {
        results.diningPreferences = await customerDataService.saveDiningPreferences(
          providerId, allData.diningPreferences
        )
      }
      
      // Sync availability
      if (allData.availability) {
        results.availability = await customerDataService.saveAvailability(
          providerId, allData.availability
        )
      }
      
      // Sync approved reps
      if (allData.approvedReps) {
        results.approvedReps = await customerDataService.saveApprovedReps(
          providerId, allData.approvedReps
        )
      }
      
      return results
    } catch (error) {
      logger.error('Error syncing provider data', { error: error.message, providerId })
      throw error
    }
  }
}

/**
 * Development helper - check if we're using mock data
 */
export const isUsingMockData = () => {
  return !process.env.REACT_APP_SUPABASE_URL || 
         process.env.REACT_APP_SUPABASE_URL === 'your-project-url-here'
}